/**
 * Starter Drawer — wraps Mantine's `<Drawer>` for the right-side
 * detail panel pattern in the design's UI kit.
 *
 * Defaults match the design (preview/comp-drawer.html-style detail panel):
 *   - position="right"
 *   - size = 480px (the detail-panel width)
 *   - scrim uses --bg-overlay
 *   - left-edge border + shadow-3 elevation
 *   - 360ms transform with the design's --ease-out curve
 *
 * Override `position` / `size` for one-off needs (e.g., the bottom-sheet
 * pattern for mobile-only flows).
 */
import { forwardRef } from 'react';
import {
  Drawer as MantineDrawer,
  type DrawerProps as MantineDrawerProps,
} from '@mantine/core';
import styles from './Drawer.module.css';

export interface DrawerProps extends MantineDrawerProps {}

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  {
    position = 'right',
    size = 480,
    classNames,
    transitionProps,
    ...rest
  },
  ref,
) {
  const existing = (classNames ?? {}) as {
    content?: string;
    overlay?: string;
    header?: string;
    title?: string;
    body?: string;
  };
  const mergedClassNames = {
    ...existing,
    content: [styles.content, existing.content].filter(Boolean).join(' '),
    overlay: [styles.overlay, existing.overlay].filter(Boolean).join(' '),
    header: [styles.header, existing.header].filter(Boolean).join(' '),
    title: [styles.title, existing.title].filter(Boolean).join(' '),
    body: [styles.body, existing.body].filter(Boolean).join(' '),
  };

  return (
    <MantineDrawer
      ref={ref}
      position={position}
      size={size}
      classNames={mergedClassNames}
      transitionProps={transitionProps ?? { duration: 360, transition: 'slide-left' }}
      {...rest}
    />
  );
});
